import { CloseRounded } from "@mui/icons-material";
import {
	Box,
	Button,
	Dialog,
	DialogActions,
	Divider,
	FormControl,
	FormControlLabel,
	FormLabel,
	IconButton,
	InputLabel,
	MenuItem,
	Radio,
	RadioGroup,
	Select,
	Stack,
	TextField,
	Typography,
} from "@mui/material";
import moment from "moment";
import { useMemo, useState } from "react";
import { AuthState } from "context/AuthContext";
import { GlobalState } from "context/GlobalContext";
import { PimDashboardState } from "context/PimDashboardContext";
import { allPOItemAssoc, poItemColorAssoc, poItemStyleAssoc, purchaseType, wearHouses } from "data/pimData";
import CommonDataGrid from "./commonDataGrid";
import { fobGeneralFields } from "./testData";
import { createPo, updatePo } from "../../fobPoApi";

const defalutValues = {
	owner_site: "select",
	date: moment(new Date()).format("YYYY-MM-DD"),
	order_no: "auto generate",
	document_no: "",
	vendor_name: "select",
	factory: "select",
	purchase_type: "select",
	valid_from: moment().format("YYYY-MM-DD"),
	valid_till: "",
	currency: "",
	exchange_rate: "",
	agent: "",
	commission_rate: "",
	transporter: "",
	merchandiser_name: "",
	trade_group: "",
	term: "",
	status: "NEW",
	channel: "select",
	delivery_date: "",
	entry_mode: "items",
};

const CommonInputFields = ({ fields, value, setInputValues, inputValues, selectedPO, edit, disabled }) => {
	const isPublished = edit && selectedPO?.is_published;
	return (
		<Stack width="30%">
			<InputLabel>
				{fields?.name} {fields?.required ? <span style={{ color: "red" }}>*</span> : ""}
			</InputLabel>
			{fields.inputType === "input" ? (
				<TextField
					disabled={disabled || isPublished}
					required
					size="small"
					value={value}
					onChange={setInputValues}
					placeholder={fields?.name}
					name={fields?.key}
				/>
			) : fields.inputType === "dropdown" ? (
				<Select
					size="small"
					value={value}
					name={fields?.key}
					disabled={disabled || isPublished}
					onChange={setInputValues}>
					<MenuItem value="select">Select</MenuItem>
					{Array.isArray(fields?.items)
						? fields.items.map((row) => (
								<MenuItem key={row?.company_name || row?.name} value={row?.company_name || row?.name}>
									{row?.company_name || row?.name}
								</MenuItem>
							))
						: Object.keys(fields?.items || {}).map((row) => {
								const values = fields?.items[row];
								return (
									<MenuItem key={values?.key} value={values?.key}>
										{values?.name}
									</MenuItem>
								);
							})}
				</Select>
			) : fields.inputType === "date" ? (
				<TextField
					size="small"
					type="date"
					value={moment(value).format("YYYY-MM-DD")}
					name={fields?.key}
					onChange={setInputValues}
					required
					disabled={disabled || isPublished}
					inputProps={{
						min:
							fields?.key === fobGeneralFields.valid_till.key
								? inputValues[fobGeneralFields.valid_from.key]
								: moment(new Date()).format("YYYY-MM-DD"),
					}}
				/>
			) : null}
		</Stack>
	);
};

const generateFOBPOItemRows = (arr) => {
	return (arr || []).map((row, index) => ({
		...row,
		id: row.id || `${index}`,
		style_name: row[poItemStyleAssoc]?.style_name,
		color: row[poItemColorAssoc]?.color,
		pallete: row[poItemColorAssoc]?.pallete,
		category: row[poItemStyleAssoc]?.category,
	}));
};

const FobPoCreationPopup = ({ modalOpen, handleCloseModal, warehouse, channel, selectedStyles, edit, selectedPO, refreshTable }) => {
	const { supplierList } = PimDashboardState();
	const { user } = AuthState();
	const { setSnackBar, snackBar, setSnackBarOpen, setLoadingOpen } = GlobalState();

	const [inputValues, setInputValues] = useState(edit ? selectedPO : { ...defalutValues, channel, owner_site: warehouse });

	const configuredFields = useMemo(() => {
		const cloned = { ...fobGeneralFields };
		cloned.purchase_type = { ...cloned.purchase_type, items: purchaseType };
		cloned.vendor_name = {
			...cloned.vendor_name,
			items: supplierList.filter((row) => row?.company_name),
		};
		cloned.factory = {
			...cloned.factory,
			items:
				supplierList.find((row) => row?.company_name === inputValues.vendor_name)?.supplierFactories || [{ name: "Main" }],
		};
		return cloned;
	}, [inputValues.vendor_name, supplierList]);

	const handleTradeGroup = (value) => {
		const blr = ["56", "57", "58", "59"];
		const ggn = ["12", "13"];
		const supplier = supplierList.find((row) => row?.company_name === inputValues.vendor_name);
		const supplierPin = (value === "Main" ? supplier?.communication_office_pin_code : supplier?.supplierFactories?.find((row) => row?.name === value)?.factory_pin_code) || "";
		const warehousePin = wearHouses?.[warehouse]?.pincode?.toString() || "";
		const supplierPrefix = supplierPin?.toString?.().substring(0, 2);
		const warehousePrefix = warehousePin.substring(0, 2);
		const localTrade = blr.includes(supplierPrefix) && blr.includes(warehousePrefix) ? "LOCAL" : ggn.includes(supplierPrefix) && ggn.includes(warehousePrefix) ? "LOCAL" : "INTRA_STATE";
		setInputValues({
			...inputValues,
			[fobGeneralFields.factory.key]: value,
			[fobGeneralFields.trade_group.key]: localTrade,
			[fobGeneralFields.term.key]: localTrade === "LOCAL" ? "IGST, CGST" : "SGST",
		});
	};

	const handleInputChange = (e) => {
		const name = e.target.name;
		const value = e.target.value;

		if (name === fobGeneralFields.valid_from.key) {
			setInputValues({ ...inputValues, [fobGeneralFields.valid_till.key]: "", [name]: value });
			return;
		}

		if (name === fobGeneralFields.vendor_name.key) {
			setInputValues({
				...inputValues,
				[fobGeneralFields.factory.key]: "select",
				[fobGeneralFields.term.key]: "",
				[fobGeneralFields.trade_group.key]: "",
				[name]: value,
			});
			return;
		}

		if (name === fobGeneralFields.factory.key) {
			handleTradeGroup(value);
			return;
		}

		setInputValues({ ...inputValues, [name]: value });
	};

	const saveDisable = Object.keys(configuredFields).some((key) => {
		const field = configuredFields[key];
		if (!field?.required) return false;
		return inputValues[key] === "" || inputValues[key] === "select";
	}) || (edit && selectedPO?.is_published);

	const buildBody = (isPub) => {
		const fobPoItems = [];
		(selectedStyles || []).forEach((style) => {
			(style?.styleColorBuying || []).forEach((color) => {
				(color?.colorBuyingQty || []).forEach((buyQty) => {
					fobPoItems.push({
						...buyQty,
						color_id: color?.color_id,
						style_id: style?.id,
					});
				});
			});
		});

		return {
			...inputValues,
			updated_by: user?.email,
			warehouse,
			is_published: !!isPub,
			fobPoItems,
		};
	};

	const handleSave = async (isPub) => {
		setLoadingOpen(true);
		try {
			if (edit) {
				await updatePo({
					...inputValues,
					updated_by: user?.email,
					warehouse,
					is_published: !!isPub,
				});
			} else {
				await createPo(buildBody(isPub));
			}
			refreshTable?.();
			setSnackBar({
				...snackBar,
				message: edit ? "Successfuly updated FOB PO" : "Successfuly created FOB PO",
				severity: "success",
				anchorOrigin: { horizontal: "center", vertical: "top" },
			});
			handleCloseModal(false);
		} catch (error) {
			setSnackBarOpen(true);
			setSnackBar({
				...snackBar,
				message: "Server Error",
				severity: "error",
				anchorOrigin: { horizontal: "center", vertical: "top" },
			});
		} finally {
			setLoadingOpen(false);
		}
	};

	return (
		<Dialog maxWidth="xl" PaperProps={{ style: { padding: "15px" } }} fullWidth className="Channel_dialog" open={modalOpen}>
			<IconButton
				aria-label="close"
				onClick={() => handleCloseModal(false)}
				sx={{
					position: "absolute",
					right: 8,
					top: 8,
					color: (theme) => theme.palette.grey[500],
				}}>
				<CloseRounded />
			</IconButton>
			<Stack display="flex" flexDirection="column" height="100%">
				<Typography fontSize={20} fontWeight={600}>
					FoB Purchase Order
				</Typography>
				<Divider />
				<Stack p={1}>
					<Typography sx={{ marginTop: "5px", fontWeight: 700, fontSize: "18px" }}>General</Typography>
					<Divider />
					<Box rowGap={2} mt={1} display="flex" columnGap="3%" flexWrap="wrap">
						{Object.keys(configuredFields).map((key) => {
							const field = configuredFields[key];
							return (
								<CommonInputFields
									key={key}
									fields={field}
									value={inputValues[key]}
									setInputValues={handleInputChange}
									inputValues={inputValues}
									selectedPO={selectedPO}
									edit={edit}
								/>
							);
						})}
					</Box>
					<FormControl sx={{ marginTop: "10px" }}>
						<FormLabel>Entry Mode</FormLabel>
						<RadioGroup row name="entry_mode" value="items" onChange={handleInputChange}>
							<FormControlLabel value="items" control={<Radio />} label="Items" />
						</RadioGroup>
					</FormControl>
				</Stack>

				<Typography sx={{ marginTop: "10px", fontWeight: 700, fontSize: "18px" }}>Items Details</Typography>
				<Divider />
				<Box width="75%" sx={{ marginTop: "20px" }} pl="10px">
					{(selectedStyles || []).map((style) => (
						<CommonDataGrid
							key={style.id}
							rows={
								edit
									? generateFOBPOItemRows(selectedPO?.[allPOItemAssoc])?.filter((row) => `${row.style_id}` === `${style.id}`)
									: (style?.styleColorBuying || []).flatMap((color) =>
											(color?.colorBuyingQty || []).map((buyQty) => ({
												...buyQty,
												id: buyQty.id || `${style.id}-${color.color_id}`,
												style_name: style.style_name,
												category: style.category,
												color: color?.buyingColor?.color,
												pallete: color?.buyingColor?.pallete,
											})),
										)
							}
							size_type={style?.size_type}
						/>
					))}
				</Box>
			</Stack>
			<Divider />
			<DialogActions sx={{ display: "flex", justifyContent: "flex-end" }}>
				<Stack direction="row">
					<Button variant="outlined" autoFocus onClick={() => handleCloseModal(false)}>
						Cancel
					</Button>
					<Button disabled={saveDisable} sx={{ margin: "0 0 0 7px" }} variant="contained" autoFocus onClick={() => handleSave(false)}>
						Save
					</Button>
					<Button sx={{ margin: "0 0 0 7px" }} variant="contained" disabled={saveDisable} onClick={() => handleSave(true)}>
						Publish
					</Button>
				</Stack>
			</DialogActions>
		</Dialog>
	);
};

export default FobPoCreationPopup;
